import h from 'hyperscript';

import arrowLeftIcon from '@disy/cadenza-icons/arrow-left.svg';
import chevronRightIcon from '@disy/cadenza-icons/chevron-right.svg';
import viewIcon from '@disy/cadenza-icons/views.svg';
import unknownVisualizationTypeIcon from '@disy/cadenza-icons/visualization-type-unknown.svg';
import { assert } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { logger } from '@disy/lib/logging';
import { uniqueId } from '@disy/lib/unique-id';
import { createButton, createIconButton } from '@disy/ui/button';
import '@disy/ui/disclosure';
import type { Disclosure } from '@disy/ui/disclosure';
import '@disy/ui/form-group';
import { icon } from '@disy/ui/icon';
import { createLabelWithIcon } from '@disy/ui/label-with-icon';
import { PLACEMENT, closePopup, setupPopupButton } from '@disy/ui/popup';
import { ProgressSpinner } from '@disy/ui/progress-spinner';
import type { SelectListOptionElement } from '@disy/ui/select-list';
import { SelectList } from '@disy/ui/select-list';

import { confirm } from 'cadenza/alert';
import { getVisualizations } from 'cadenza/api-client/workbook-api';
import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import { createVisualizationTypeLabel } from 'cadenza/ui/visualization-type-label/visualization-type-label';
import { visualizationIcon } from 'cadenza/workbook/api/data-view-visualization-key';
import { viewTypeI18n, viewTypeIcon } from 'cadenza/workbook/api/view-type';
import type { DataView } from 'cadenza/workbook/data-view/data-view';
import { getViewType } from 'cadenza/workbook/data-view/data-view-utils';
import { CombinationChartDataViewVisualizationKeys } from 'cadenza/workbook/model/combination-chart-visualization-types.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { VisualizationCategory } from 'cadenza/workbook/model/visualization-category.gen';
import type { VisualizationDto } from 'cadenza/workbook/model/visualization-dto';
import type { View } from 'cadenza/workbook/view/view';
import { minimizeViewIfMaximized } from 'cadenza/workbook/view/view-util';
import 'cadenza/workbook/visualisation-picker/actionable-list-item/actionable-list-item.css';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  getActiveWorksheetId,
  getAttributeBundleSettings,
  getDataViewStatus,
  getNoneAnalyticLineDataViewIds,
  getWorkbookNameAndHash,
  isCombinationChart,
} from 'cadenza/workbook/workbook-core-selectors.state';

import './visualization-picker.css';
import i18n from './visualization-picker.properties';

const MULTIPLE_VISUALIZATIONS_PICKER = 'd-multiple-visualizations-picker';
const VISUALIZATION_PICKER = 'd-visualization-picker';

export type VisualizationPickerButton = HTMLButtonElement & { update: () => void };
type VisualizationElement = HTMLElement & { dVisualization: VisualizationDto };

export function createVisualizationPickerButton(view: View, asIconButton: boolean) {
  const button = createVisualizationButtonInternal(asIconButton) as VisualizationPickerButton;
  button.update = () => {
    const dataViews = view.dataViews;
    const pickerIcon = dataViews.length === 1 ? visualizationIcon(dataViews[0].visualizationKey) : icon(viewIcon);
    button.firstElementChild!.replaceWith(pickerIcon);
  };
  return setupVisualizationPickerFlyout(button, view);
}

function createVisualizationButtonInternal(asIconButton: boolean) {
  if (asIconButton) {
    return createIconButton(icon(viewIcon), i18n('visualizations.button.label'), {
      variant: 'borderless',
      size: 's',
    });
  }
  return createButton([icon(viewIcon), i18n('visualizations.button.label')], {
    attrs: { role: 'menuitem' },
  });
}

class MultipleVisualizationsPicker extends HTMLElement {
  #view;

  constructor(view: View) {
    super();
    this.#view = view;
  }

  connectedCallback() {
    this.render();
  }

  render() {
    this.classList.add(MULTIPLE_VISUALIZATIONS_PICKER);
    this.innerHTML = '';

    const dataViewSelect = new SelectList<DataView>(
      this.#view.dataViews.map((dataView) =>
        h<SelectListOptionElement<DataView>>(
          'li.d-actionable-list-item',
          { selectListValue: dataView },
          h('.d-actionable-list-item--content', visualizationIcon(dataView.visualizationKey), h('span', dataView.name)),
          icon(chevronRightIcon),
        ),
      ),
    );
    dataViewSelect.setAttribute('aria-label', i18n('visualizationPicker.visualization.manageButton'));
    on(dataViewSelect, ['pointerdown', 'keydown'], (event: KeyboardEvent) => {
      if (event.type === 'pointerdown' || event.key === 'Enter') {
        const dataView = dataViewSelect.selection[0];
        if (dataView) {
          this._renderDataviewVisualizationPicker(dataView);
        }
      }
    });

    this.append(
      h('.d-compact-header', h('.d-compact-header--title', i18n('visualizationPicker.selectDataView'))),
      dataViewSelect,
    );
  }

  _renderDataviewVisualizationPicker(dataView: DataView) {
    const header = this._getGoBackHeader(dataView);
    const content = new VisualizationPicker(dataView, {
      visualizationSelectFn: multipleDataViewsVisualizationSelectFn,
    });
    this.innerHTML = '';
    this.append(header, content);
  }

  _getGoBackHeader(dataView: DataView) {
    return h('.d-compact-header', [
      createIconButton(icon(arrowLeftIcon), i18n('visualizationPicker.visualization.backButton'), {
        variant: 'borderless',
        size: 's',
        onclick: () => this.render(),
      }),
      h('.d-compact-header--title', i18n('visualizationPicker.header', { name: dataView.name })),
    ]);
  }
}

customElements.define(MULTIPLE_VISUALIZATIONS_PICKER, MultipleVisualizationsPicker);

class VisualizationPicker extends HTMLElement {
  readonly #dataView;

  constructor(
    dataView: DataView,
    {
      visualizationSelectFn,
      picker,
    }: {
      visualizationSelectFn?: (dataView: DataView, visualization: VisualizationDto) => void;
      picker?: VisualizationPickerButton;
    },
  ) {
    super();
    this.#dataView = dataView;
    assert(visualizationSelectFn || picker, 'Either visualizationSelectFn or picker are required');
    on(
      this,
      'click',
      (event) => {
        closePopup();
        const visualization = (event.delegateTarget as VisualizationElement).dVisualization;
        if (visualizationSelectFn) {
          visualizationSelectFn(dataView, visualization);
        } else {
          changeVisualization(dataView, visualization);
          picker!.update();
        }
      },
      { delegate: '.visualization' },
    );
  }

  connectedCallback() {
    this.id = uniqueId();
    this._render(this.#dataView);
  }

  _render(dataView: DataView) {
    const flyout = this.parentElement!;
    const progressSpinner = new ProgressSpinner({
      label: i18n('visualizations.loading'),
      vertical: true,
    });

    getVisualizationsMap(dataView.dataViewId)
      .then((visualizationsMap) => {
        progressSpinner.replaceWith(createFlyoutContent(visualizationsMap));

        const selectedVisualizationKey = this.#dataView.visualizationKey;
        this.querySelectorAll<VisualizationElement>('.visualization').forEach((visualizationEl) => {
          const dVisualization = visualizationEl.dVisualization;
          const isSelected =
            dVisualization.key === selectedVisualizationKey &&
            (!dVisualization.visualizationVariant ||
              // when variant is present then we need to check current bundle settings to mark the correct one as selected
              (dVisualization.visualizationVariant === 'EXTENDED' && this.#isOptionalBundleOn(dataView.dataViewId)) ||
              (dVisualization.visualizationVariant === 'SIMPLE' && !this.#isOptionalBundleOn(dataView.dataViewId)));
          if (isSelected) {
            visualizationEl.closest<Disclosure>('.visualization-group')!.expanded = true;
          }
          visualizationEl.setAttribute('aria-selected', String(isSelected));
        });
      })
      .catch((error) => {
        logger.error('Failed to load visualizations', error);
        const alert = h('d-alert', {
          message: i18n('visualizations.serverError'),
          type: 'error',
        });
        progressSpinner.replaceWith(alert);
      })
      .finally(() => {
        flyout.removeAttribute('aria-describedby');
        flyout.removeAttribute('aria-busy');
      });

    this.append(progressSpinner);
    flyout.setAttribute('aria-busy', 'true');
    flyout.setAttribute('aria-describedby', progressSpinner.id);
  }

  #isOptionalBundleOn(dataViewId: DataViewId) {
    return (
      getAttributeBundleSettings(getStore().getState(), dataViewId, 'COLOR_CLASSIFICATION')?.['bundle.visible'] ===
      'true'
    );
  }
}

function multipleDataViewsVisualizationSelectFn(dataView: DataView, visualization: VisualizationDto) {
  const isMultiDataViewChart = isCombinationChart(getStore().getState(), dataView.viewId);

  if (
    getViewType(dataView) === visualization.type &&
    !(isMultiDataViewChart && !CombinationChartDataViewVisualizationKeys.values.includes(visualization.key))
  ) {
    changeVisualization(dataView, visualization);
    return;
  }

  const viewVariant = isMultiDataViewChart ? 'COMBINATION_CHART' : 'MAP';

  return confirm(
    i18n(`visualizationPicker.confirmHeader.<${viewVariant}>`, { name: dataView.name }),
    i18n(`visualizationPicker.confirmMessage.<${viewVariant}>`),
    {
      type: 'warning',
      submit: i18n('visualizationPicker.confirmCreateNew'),
      alternativeSubmit: i18n('visualizationPicker.confirmDiscardOthers'),
      submitCallback: () => {
        getStore().dispatch(
          executeCommand(
            Command.EXTRACT_DATA_VIEW_TO_NEW_VIEW({
              worksheetId: getActiveWorksheetId(getStore().getState()),
              dataViewId: dataView.dataViewId,
              visualizationKey: visualization.key,
            }),
          ),
        );
        minimizeViewIfMaximized(dataView.dataViewId);
      },
      alternativeSubmitCallback: () => changeVisualization(dataView, visualization),
    },
  );
}

export function getVisualizationsMap(dataViewId?: DataViewId) {
  return getVisualizations(getWorkbookNameAndHash(getStore().getState()), dataViewId).then((visualizations) => {
    const sortedVisualizationsMap = new Map<VisualizationCategory, VisualizationDto[]>();
    VisualizationCategory.values.forEach((key) => {
      if (visualizations[key]) {
        const visualizationsSet = orderWithExtensionTypesAlphabetically(visualizations[key]);
        sortedVisualizationsMap.set(key, visualizationsSet);
      }
    });
    return sortedVisualizationsMap;
  });
}

function orderWithExtensionTypesAlphabetically(visualizations: VisualizationDto[]) {
  const orderedVisualizations = visualizations.filter((visualization) => visualization.type !== 'EXTENSION');
  const orderedExtensionVisualizations = visualizations
    .filter((visualization) => visualization.type === 'EXTENSION')
    .sort((a, b) => (a.name < b.name ? -1 : 1));
  orderedVisualizations.push(...orderedExtensionVisualizations);
  return orderedVisualizations;
}

customElements.define(VISUALIZATION_PICKER, VisualizationPicker);

function changeVisualization(dataView: DataView, visualization: VisualizationDto) {
  return dataView.changeVisualization(visualization.key, visualization.visualizationVariant);
}

function createFlyoutContent(visualizationsMap: Map<VisualizationCategory, VisualizationDto[]>) {
  return h(
    '.d-stack-v.space-4',
    [...visualizationsMap].map(([key, visualizations]) =>
      createCollapsiblePanel({
        title: i18n(`visualization.${key}`),
        expanded: key === 'RECOMMENDED',
        visualizations,
      }),
    ),
  );
}

function createCollapsiblePanel({
  title = 'Label',
  expanded = false,
  visualizations = [],
}: {
  title?: string;
  expanded?: boolean;
  visualizations?: VisualizationDto[];
} = {}) {
  return h('d-disclosure.d-workbook-section.visualization-group', { expanded }, [
    h('summary', title),
    h('.visualization-group--content', visualizations.map(createVisualization)),
  ]);
}

export function createVisualization(visualization: VisualizationDto) {
  const visualizationEl = h<VisualizationElement>(
    '.visualization.focus-outline-keynav.keyboard-click',
    {
      attrs: { role: 'option' },
      tabIndex: 0,
    },
    [
      h('.visualization--icon-wrapper', visualizationIcon(visualization.key, { svgSize: 'lg' })),
      h('.visualization--label', getVisualizationName(visualization)),
    ],
  );
  visualizationEl.dVisualization = visualization;
  return visualizationEl;
}

export function getVisualizationName(visualization: VisualizationDto) {
  return visualization.visualizationVariant && visualization.visualizationVariant !== 'BASE' ?
      i18n(`visualizationPicker.nameWithVariant.<${visualization.visualizationVariant}>`, {
        name: visualization.name,
      })
    : visualization.name;
}

export function createSelectLikePicker(view: View, dataView: DataView, isDisabled = false) {
  const picker = h<VisualizationPickerButton>(
    `.${VISUALIZATION_PICKER}--select-button.keyboard-click.focus-outline-keynav.form-group-control.d-stack-h.space-2`,
    {
      attrs: { role: 'button' },
      tabIndex: isDisabled ? -1 : 0,
    },
  );

  if (isDisabled) {
    picker.inert = true;
  }

  picker.checkValidity = () => {
    if (dataView && getDataViewStatus(getStore().getState(), dataView.dataViewId) === 'UNKNOWN_VISUALIZATION') {
      picker.dispatchEvent(new CustomEvent('invalid'));
      return false;
    }
    return true;
  };

  // Will be called when user selects a visualization
  picker.update = () => {
    // Note: this logic should be in the callback because dataView is mutable,
    // and we must use its latest properties when update() is called.
    picker.innerHTML = '';

    let visIcon, label;
    if (!dataView) {
      // text and image views have no data view attached
      const viewTypeLabel = createLabelWithIcon(viewTypeIcon(view.type, { label: undefined }), viewTypeI18n(view.type));
      viewTypeLabel.classList.add('ellipsis');
      picker.append(viewTypeLabel);
      picker.removeAttribute('data-validation-message');
    } else if (getDataViewStatus(getStore().getState(), dataView.dataViewId) === 'UNKNOWN_VISUALIZATION') {
      visIcon = icon(unknownVisualizationTypeIcon);
      label = h('.selected-visualization-label', i18n('visualizationPicker.unknownVisualization'));
      picker.setAttribute('data-validation-message', i18n('visualizationPicker.typeNotFound'));
      picker.append(visIcon, label);
    } else {
      const selectedVisualizationLabel = createVisualizationTypeLabel(
        dataView.visualizationKey,
        dataView.visualizationName,
      );
      selectedVisualizationLabel.classList.add('ellipsis');
      picker.append(selectedVisualizationLabel);
      picker.removeAttribute('data-validation-message');
    }
  };

  if (isDisabled) {
    return picker;
  }
  return setupVisualizationPickerFlyout(picker, view, dataView);
}

function setupVisualizationPickerFlyout(picker: VisualizationPickerButton, view: View, dataView?: DataView) {
  picker.classList.add(VISUALIZATION_PICKER);
  setupPopupButton(
    picker,
    (target, flyout) => {
      // Wait till setup will be finished and popup appears.
      setTimeout(() => {
        flyout.tabIndex = -1;
        flyout.focus();
      });
      return getPickerContent(picker, view, dataView);
    },
    {
      role: 'listbox',
      styleClass: `${VISUALIZATION_PICKER}--flyout`,
      placement: PLACEMENT.BOTTOM_START,
    },
  );
  return picker;
}

function getPickerContent(picker: VisualizationPickerButton, view: View, activeDataView?: DataView) {
  // This means we're in layer settings and want to change visualization for specific view
  if (activeDataView) {
    const selectFn =
      getNoneAnalyticLineDataViewIds(getStore().getState(), view.viewId).length > 1 ?
        multipleDataViewsVisualizationSelectFn
      : undefined;
    return new VisualizationPicker(activeDataView, { visualizationSelectFn: selectFn, picker });
  }
  const dataViews = view.dataViews;
  return dataViews.length === 1 ?
      new VisualizationPicker(dataViews[0], { picker })
    : new MultipleVisualizationsPicker(view);
}
